/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Match boosting", description = "Increase match rates for your audiences in advertising platforms.", lastUpdatedDate = new Date(1682985600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Match boosting",
        description: "Increase match rates for your audiences in advertising platforms.",
        lastUpdatedDate: "Tue May 02 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Implementation steps",
          "depth": 0,
          "id": "implementation-steps"
        }, {
          "value": "Model configuration",
          "depth": 1,
          "id": "model-configuration"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      strong: "strong",
      a: "a",
      h2: "h2",
      ol: "ol",
      li: "li",
      h3: "h3",
      img: "img"
    }, _provideComponents(), props.components), {BtOnly} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    return _jsxs(_Fragment, {
      children: [_jsx(BtOnly, {
        featureName: "Match Booster",
        extraText: ". It can be used with or without Customer Studio."
      }), "\n", _jsxs(_components.p, {
        children: ["Match Booster improves ", _jsx(_components.strong, {
          children: "match rates"
        }), " on ", _jsx(_components.a, {
          href: "#supported-destinations",
          children: "various paid advertising platforms"
        }), " by providing additional user identifiers. Learn more in the main ", _jsx(_components.a, {
          href: "/match-booster/overview",
          children: "Match Booster documentation"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "implementation-steps",
        children: "Implementation steps"
      }), "\n", _jsx(_components.p, {
        children: "Using Match Booster is a two-step process:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Enable and configure match boosting on relevant models."
        }), "\n", _jsxs(_components.li, {
          children: ["Configure match boosting in sync configurations to ", _jsx(_components.a, {
            href: "/match-booster/overview#supported-destinations",
            children: "supported destinations"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "model-configuration",
        children: "Model configuration"
      }), "\n", _jsx(_components.p, {
        children: "Match boosting begins with your model. By enabling and configuring Match Booster on particular models, you can enrich them with additional identifiers from Hightouch's data partners."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["To get started, go to your ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/models/",
            children: [_jsx(_components.strong, {
              children: "Models"
            }), " overview page"]
          }), ". If you want to enable matching boosting on an audience, go to its parent model on the ", _jsx(_components.strong, {
            children: "Schema"
          }), " page."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the model on which you want to enable Match Booster and open the ", _jsx(_components.strong, {
            children: "Match boosting"
          }), " tab."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "models/match-boosting.png",
          alt: "Match Boosting in models"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsxs(_components.li, {
          children: ["Toggle ", _jsx(_components.strong, {
            children: "Enable match boosting"
          }), " on."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the identifiers you want Hightouch to use to find additional data. This includes selecting an ", _jsx(_components.strong, {
            children: "identifier type"
          }), " and mapping it to a ", _jsx(_components.strong, {
            children: "column name"
          }), " in your model. The data in the columns you select should ", _jsx(_components.a, {
            href: "/match-booster/faqs#what-format-should-source-identifiers-use",
            children: "have unhashed data"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Save"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Model enrichment—the process of adding additional identifiers to your model\nresults—can take up to a few hours to process."
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've enabled match boosting on a model, you can configure ", _jsx(_components.a, {
          href: "/syncs/match-boosting",
          children: "match boosting on syncs"
        }), " based on the model."]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Check out the main ", _jsx(_components.a, {
            href: "/match-booster/faqs",
            children: "Match Booster documentation"
          }), " page for\nFAQs."]
        })
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
